import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { ProfilService } from './profil-service';

export interface ThemeOption {
  name: string;
  value: string;
  icon: string;
  isDark: boolean;
  color: string;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private currentThemeSubject = new BehaviorSubject<string>('aura-light-blue');
  public currentTheme$: Observable<string> = this.currentThemeSubject.asObservable();

  private readonly themes: ThemeOption[] = [
    { name: 'Bleu sombre', value: 'aura-dark-blue', icon: 'pi pi-moon', isDark: true, color: '#60a5fa' },
    { name: 'Bleu clair', value: 'aura-light-blue', icon: 'pi pi-sun', isDark: false, color: '#3B82F6' },
    { name: 'Vert sombre', value: 'aura-dark-teal', icon: 'pi pi-moon', isDark: true, color: '#2dd4bf' },
    { name: 'Vert clair', value: 'aura-light-teal', icon: 'pi pi-sun', isDark: false, color: '#14b8a6' },
    { name: 'Violet sombre', value: 'aura-dark-purple', icon: 'pi pi-moon', isDark: true, color: '#a78bfa' },
    { name: 'Violet clair', value: 'aura-light-purple', icon: 'pi pi-sun', isDark: false, color: '#8B5CF6' },
    { name: 'Vert sombre', value: 'aura-dark-green', icon: 'pi pi-moon', isDark: true, color: '#34d399' },
    { name: 'Vert clair', value: 'aura-light-green', icon: 'pi pi-sun', isDark: false, color: '#10b981' }
  ];

  constructor(@Inject(DOCUMENT) private document: Document, private profilService: ProfilService) {
    console.log('ThemeService initialized');

    
    
    // Try to load theme from employee profile first, then localStorage
    let savedTheme = 'aura-light-blue'; // default
    
    // First check if employee has a theme preference
    try {
      const currentEmployee = this.profilService.ProfilCourant();
      if (currentEmployee?.theme && this.themes.some(t => t.value === currentEmployee.theme)) {
        savedTheme = currentEmployee.theme;
        console.log(`Using employee theme preference: ${savedTheme}`);
      } else {
        // Fall back to localStorage if no employee theme is set
        const saved = localStorage.getItem('selected-theme');
        if (saved && this.themes.some(t => t.value === saved)) {
          savedTheme = saved;
          console.log(`Using localStorage theme: ${savedTheme}`);
        }
      }
    } catch (e) {
      console.warn('Could not load theme from employee profile or localStorage:', e);
    }
    
    // Initialize with saved theme or default
    this.setTheme(savedTheme);
    
    // Handle DOM ready state and ensure data-theme attribute is set
    this.handleDOMReady();
    
    // Set initial data-theme attribute based on the theme's dark/light property
    // Use a small delay to ensure the DOM is ready
    setTimeout(() => {
      const initialTheme = this.themes.find(t => t.value === savedTheme);
      if (initialTheme) {
        this.document.documentElement.setAttribute('data-theme', initialTheme.isDark ? 'dark' : 'light');
        console.log(`Initial data-theme set to: ${initialTheme.isDark ? 'dark' : 'light'}`);
      }
      
      // Start theme sync to ensure data-theme attribute is always in sync
      this.startThemeSync();
      
      // Debug theme state for troubleshooting
      this.debugThemeState();
    }, 100);
  }

  getThemes(): ThemeOption[] {
    return this.themes;
  }

  getCurrentTheme(): string {
    return this.currentThemeSubject.value;
  }

  setTheme(themeValue: string): void {
    try {
      const theme = this.themes.find(t => t.value === themeValue);
      if (!theme) {
        console.warn(`Theme ${themeValue} not found`);
        return;
      }

      // Create and add new theme link (but don't remove old one yet)
      const linkElement = this.document.createElement('link');
      linkElement.rel = 'stylesheet';
      linkElement.href = `assets/themes/${themeValue}/theme.css`;
      linkElement.id = 'theme-stylesheet-new';
      
      console.log(`Attempting to load theme from: assets/themes/${themeValue}/theme.css`);
      
      // Add error handling for the link
      linkElement.onerror = () => {
        console.error(`Failed to load theme: ${themeValue} from assets/themes/${themeValue}/theme.css`);
        // Remove the failed link
        if (linkElement.parentNode) {
          linkElement.parentNode.removeChild(linkElement);
        }
      };
      
      linkElement.onload = () => {
        console.log(`Theme loaded successfully: ${themeValue}`);
        
        // Now that new theme is loaded, remove the old one
        this.removeExistingTheme();
        
        // Rename the new theme link to the standard ID
        linkElement.id = 'theme-stylesheet';
        
        // Update current theme
        this.currentThemeSubject.next(themeValue);
        
        // Save to localStorage as fallback
        try {
          localStorage.setItem('selected-theme', themeValue);
        } catch (e) {
          console.warn('Could not save theme to localStorage:', e);
        }
        
        // Update employee theme preference if employee is available
        try {
          const currentEmployee = this.profilService.ProfilCourant();
          if (currentEmployee) {
            currentEmployee.theme = themeValue;
            console.log(`Updated employee theme preference to: ${themeValue}`);
          }
        } catch (e) {
          console.warn('Could not update employee theme preference:', e);
        }
        
        // Update body class for dark/light mode
        this.document.body.classList.remove('dark-mode', 'light-mode');
        this.document.body.classList.add(theme.isDark ? 'dark-mode' : 'light-mode');
        
        // Set data-theme attribute on HTML element for CSS custom properties
        this.document.documentElement.setAttribute('data-theme', theme.isDark ? 'dark' : 'light');
        
        console.log(`Theme changed to: ${themeValue}, data-theme set to: ${theme.isDark ? 'dark' : 'light'}`);
      };
      
      this.document.head.appendChild(linkElement);
      
    } catch (error) {
      console.error('Error setting theme:', error);
    }
  }

  private removeExistingTheme(): void {
    try {
      // Only remove the old theme, not the new one that's loading
      const existingTheme = this.document.getElementById('theme-stylesheet');
      if (existingTheme && existingTheme.id === 'theme-stylesheet') {
        existingTheme.remove();
      }
    } catch (error) {
      console.warn('Error removing existing theme:', error);
    }
  }

  toggleDarkMode(): void {
    try {
      const currentTheme = this.getCurrentTheme();
      const currentThemeOption = this.themes.find(t => t.value === currentTheme);
      
      if (currentThemeOption?.isDark) {
        // Switch to light version of current theme
        const lightTheme = this.themes.find(t => 
          t.value === currentTheme.replace('dark', 'light') && !t.isDark
        );
        if (lightTheme) {
          this.setTheme(lightTheme.value);
        }
      } else {
        // Switch to dark version of current theme
        const darkTheme = this.themes.find(t => 
          t.value === currentTheme.replace('light', 'dark') && t.isDark
        );
        if (darkTheme) {
          this.setTheme(darkTheme.value);
        }
      }
    } catch (error) {
      console.error('Error toggling dark mode:', error);
    }
  }

  public isDarkMode(): boolean {
    try {
      const currentTheme = this.getCurrentTheme();
      const themeOption = this.themes.find(t => t.value === currentTheme);
      return themeOption?.isDark || false;
    } catch (error) {
      console.error('Error checking dark mode:', error);
      return false;
    }
  }

  /**
   * Manually set the data-theme attribute on the HTML element
   * This is useful for external components that need to know the current theme state
   */
  setDataThemeAttribute(): void {
    try {
      const currentTheme = this.getCurrentTheme();
      const themeOption = this.themes.find(t => t.value === currentTheme);
      if (themeOption) {
        this.document.documentElement.setAttribute('data-theme', themeOption.isDark ? 'dark' : 'light');
        console.log(`data-theme attribute set to: ${themeOption.isDark ? 'dark' : 'light'}`);
      }
    } catch (error) {
      console.error('Error setting data-theme attribute:', error);
    }
  }

  /**
   * Get the current theme's data-theme value ('dark' or 'light')
   */
  getCurrentDataTheme(): string {
    try {
      const currentTheme = this.getCurrentTheme();
      const themeOption = this.themes.find(t => t.value === currentTheme);
      return themeOption?.isDark ? 'dark' : 'light';
    } catch (error) {
      console.error('Error getting current data-theme:', error);
      return 'light'; // fallback
    }
  }

  /**
   * Ensure the data-theme attribute is properly set on the HTML element
   * This method can be called to sync the attribute if it gets out of sync
   */
  ensureDataThemeAttribute(): void {
    try {
      const currentTheme = this.getCurrentTheme();
      const themeOption = this.themes.find(t => t.value === currentTheme);
      if (themeOption) {
        const expectedDataTheme = themeOption.isDark ? 'dark' : 'light';
        const currentDataTheme = this.document.documentElement.getAttribute('data-theme');
        
        if (currentDataTheme !== expectedDataTheme) {
          this.document.documentElement.setAttribute('data-theme', expectedDataTheme);
          console.log(`data-theme attribute synced from '${currentDataTheme}' to '${expectedDataTheme}'`);
        }
      }
    } catch (error) {
      console.error('Error ensuring data-theme attribute:', error);
    }
  }

  /**
   * Start listening for theme changes and automatically sync the data-theme attribute
   * This ensures the attribute is always in sync even if changed externally
   */
  startThemeSync(): void {
    try {
      // Subscribe to theme changes and ensure data-theme attribute is set
      this.currentTheme$.subscribe(themeValue => {
        const theme = this.themes.find(t => t.value === themeValue);
        if (theme) {
          this.document.documentElement.setAttribute('data-theme', theme.isDark ? 'dark' : 'light');
          console.log(`Theme changed to: ${themeValue}, data-theme synced to: ${theme.isDark ? 'dark' : 'light'}`);
        }
      });
      
      console.log('Theme sync started');
    } catch (error) {
      console.error('Error starting theme sync:', error);
    }
  }

  /**
   * Handle DOM ready state and ensure data-theme attribute is set
   * This is useful for cases where the service is initialized before the DOM is ready
   */
  handleDOMReady(): void {
    try {
      if (this.document.readyState === 'loading') {
        // DOM is still loading, wait for it
        this.document.addEventListener('DOMContentLoaded', () => {
          console.log('DOM content loaded, ensuring data-theme attribute');
          this.ensureDataThemeAttribute();
        });
      } else {
        // DOM is already ready
        console.log('DOM already ready, ensuring data-theme attribute');
        this.ensureDataThemeAttribute();
      }
    } catch (error) {
      console.error('Error handling DOM ready state:', error);
    }
  }

  /**
   * Debug method to show current theme state
   */
  debugThemeState(): void {
    try {
      const currentTheme = this.getCurrentTheme();
      const themeOption = this.themes.find(t => t.value === currentTheme);
      const currentDataTheme = this.document.documentElement.getAttribute('data-theme');
      const bodyClasses = this.document.body.className;
      
      console.log('=== Theme Debug Info ===');
      console.log('Current theme:', currentTheme);
      console.log('Theme isDark:', themeOption?.isDark);
      console.log('Expected data-theme:', themeOption?.isDark ? 'dark' : 'light');
      console.log('Actual data-theme:', currentDataTheme);
      console.log('Body classes:', bodyClasses);
      console.log('========================');
    } catch (error) {
      console.error('Error debugging theme state:', error);
    }
  }
}
